import React, { ButtonHTMLAttributes, useMemo } from 'react'
import styled, { css, StyledProps } from 'styled-components'
import { Progress } from '../../../globalTypes'
import { Spinner } from '../../misc/Spinner/Spinner'

export type ButtonIntent =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'cancel'
  | 'action'
  | 'warning'
  | 'clear'
  | 'minor-action'
  | 'primary-action'
export type ButtonVariant = 'text' | 'outlined' | 'filled' | 'no-style'
export type ButtonSize = 'small' | 'medium' | 'big'

export type ButtonProps = {
  intent?: ButtonIntent
  disabled?: boolean
  size?: ButtonSize
  /**
   * @deprecated use `variant` instead
   */
  filled?: boolean
  progress?: Progress
  readOnly?: boolean
  variant?: ButtonVariant
}
type StyledButtonProps = StyledProps<ButtonProps>

export type ButtonFullProps = ButtonProps & ButtonHTMLAttributes<HTMLButtonElement>

const buttonColorCss = ({ intent, theme, variant }: StyledButtonProps) => {
  if (variant === 'no-style')
    return css`
      background: none;
      border: none;
    `

  let backgroundColor = variant !== 'text' ? theme.color.onPrimaryLight : 'transparent'
  let borderColor = 'transparent'
  let color = 'inherit'
  let focusBackgroundColor = 'transparent'

  const filled = variant === 'filled'
  const outlined = variant === 'outlined'

  switch (intent) {
    case 'primary':
      if (outlined) borderColor = theme.color.primary600

      if (filled) {
        backgroundColor = theme.color.primary600
        color = theme.color.onPrimaryLight
        focusBackgroundColor = theme.color.primary500
      } else {
        color = theme.color.primary600
        focusBackgroundColor = theme.color.primary25
      }
      break
    case 'secondary':
      if (outlined) borderColor = theme.color.primary600

      if (filled) {
        backgroundColor = theme.color.primary25
        color = theme.color.primary600
        focusBackgroundColor = theme.color.primary5
      } else {
        color = theme.color.primary600
        focusBackgroundColor = theme.color.primary5
      }
      break
    case 'warning':
      if (outlined) borderColor = theme.color.accentNotify

      if (filled) {
        backgroundColor = theme.color.accentNotify
        color = theme.color.onPrimaryLight
        focusBackgroundColor = theme.color.accentNotify + 'BB'
      } else {
        color = theme.color.accentNotify
        focusBackgroundColor = theme.color.accentNotify + '22'
      }
      break
    case 'danger':
      if (outlined) borderColor = theme.color.accentDestructive

      if (filled) {
        backgroundColor = theme.color.accentDestructive
        color = theme.color.onPrimaryLight
        focusBackgroundColor = theme.color.accentDestructive + 'BB'
      } else {
        color = theme.color.accentDestructive
        focusBackgroundColor = theme.color.accentDestructive + '22'
      }
      break
    case 'cancel':
      if (outlined) borderColor = theme.color.secondary200

      if (filled) {
        backgroundColor = theme.color.secondary200
        color = theme.color.onPrimaryLight
        focusBackgroundColor = theme.color.secondary200 + 'BB'
      } else {
        backgroundColor = theme.color.onPrimaryLight
        color = theme.color.secondary200
        focusBackgroundColor = theme.color.secondary200 + '22'
      }
      break
    case 'action':
      borderColor = theme.color.accentApproving

      if (filled) {
        backgroundColor = theme.color.accentApproving
        color = theme.color.onPrimaryLight
        focusBackgroundColor = theme.color.accentApproving + 'BB'
      } else {
        color = theme.color.accentApproving
        focusBackgroundColor = theme.color.accentApproving + '22'
      }
      break
    case 'primary-action':
      borderColor = theme.color.accentApproving100

      if (filled) {
        backgroundColor = theme.color.accentApproving100
        color = theme.color.onPrimaryLight
        focusBackgroundColor = theme.color.accentApproving100 + 'BB'
      } else {
        color = theme.color.accentApproving100
        focusBackgroundColor = theme.color.accentApproving100 + '22'
      }
      break
    case 'minor-action':
      if (outlined) borderColor = 'transparent'

      if (filled) {
        backgroundColor = theme.color.primary25
        color = theme.color.primary600
        focusBackgroundColor = theme.color.primary25 + 'BB'
      } else {
        color = theme.color.primary600
        focusBackgroundColor = theme.color.primary25 + '22'
      }
      break
    case 'clear':
      if (outlined) borderColor = theme.color.secondary50

      if (filled) {
        backgroundColor = theme.color.secondary50
        color = theme.color.primary600
        focusBackgroundColor = theme.color.secondary50 + 'BB'
      } else {
        color = theme.color.primary600
        focusBackgroundColor = theme.color.secondary50 + '22'
      }
      break
    default:
      break
  }

  return css`
    background-color: ${backgroundColor};
    border-color: ${borderColor};
    color: ${color};
    svg {
      fill: ${color};
    }
    &:hover {
      background-color: ${focusBackgroundColor};
    }
    &:focus {
      background-color: ${focusBackgroundColor};
      box-shadow: 0 0 4px ${borderColor};
    }
  `
}

const buttonSizeCss = ({ size = 'medium', variant }: StyledButtonProps) => {
  if (variant === 'no-style')
    return css`
      padding: 0;
      height: auto;
    `
  switch (size) {
    case 'small':
      return css`
        height: 32px;
        padding: 6px 12px;
      `
    case 'medium':
      return css`
        height: 40px;
        padding: 10px 24px;
      `
    case 'big':
      return css`
        padding: 24px;
        height: 48px;
        min-width: 160px;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
      `
  }
}

const buttonVariantCss = ({ variant }: StyledButtonProps) => {
  switch (variant) {
    case 'text':
      return css`
        padding: 0;
        background-color: transparent !important;
        height: auto;
        & svg {
          width: 24px;
          height: 24px;
        }
        &:hover {
          opacity: 0.8;
        }
      `
    case 'no-style':
      return css``
    case 'outlined':
    case 'filled':
      return css`
        border-width: 1px;
        border-style: solid;
        border-radius: 8px;
        & svg {
          width: 1em;
          height: 1em;
        }
      `
  }
}

const ButtonStyled = styled.button<StyledButtonProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;

  line-height: 20px;
  outline: none;
  cursor: pointer;
  transition: 0.1s background-color ease-out;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  pointer-events: ${(props: StyledButtonProps) => (props.readOnly ? 'none' : 'auto')};

  ${buttonColorCss}
  ${buttonSizeCss}
  ${buttonVariantCss}

  a {
    color: unset;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const ButtonContent = styled.div<{ hide: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${props => (props.hide ? 'hidden' : 'visible')};
  gap: 8px;
`

const SpinnerStyled = styled(Spinner)`
  position: absolute;
`

export const Button: React.FC<ButtonFullProps> = ({
  children,
  progress,
  size = 'medium',
  type = 'button',
  filled,
  readOnly,
  intent,
  variant = filled ? 'filled' : 'outlined',
  ...buttonProps
}) => {
  const showSpinner = progress === Progress.WORK
  const spinnerFilled = useMemo(
    () => (filled && intent && ['clear', 'minor-action', 'secondary'].includes(intent)) || !filled,
    [filled, intent],
  )
  return (
    <ButtonStyled
      variant={variant}
      type={type}
      size={size}
      filled={filled}
      readOnly={readOnly}
      intent={intent}
      {...buttonProps}
    >
      {showSpinner ? <SpinnerStyled size={'small'} filled={spinnerFilled} /> : null}
      <ButtonContent hide={showSpinner}>{children}</ButtonContent>
    </ButtonStyled>
  )
}
